import { Link } from 'react-router-dom';
import {
  BookOpen,
  Layers,
  RotateCcw,
  Users,
  ArrowRightLeft,
  Code2,
  ChevronRight,
  Search,
  Workflow,
  FileText,
  Zap,
} from 'lucide-react';
import { SystemMapDiagram } from './components/diagrams/SystemMapDiagram';

interface AreaCard {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  iconBg: string;
  border: string;
  tag: string;
}

const areas: AreaCard[] = [
  {
    title: 'Item Management',
    description: 'Create, enrich, and manage work items. Understand item lifecycle, relationships, readiness, and collections.',
    href: '/prioritization/documentation/items',
    icon: Layers,
    color: 'text-primary-700',
    iconBg: 'bg-primary-50 border-primary-200',
    border: 'border-primary-200 hover:border-primary-400',
    tag: 'Always-on',
  },
  {
    title: 'Cycle Design & Execution',
    description: 'Configure cycles, define scope and tracks, and govern a prioritization effort from setup through published priorities.',
    href: '/prioritization/documentation/cycles',
    icon: RotateCcw,
    color: 'text-[var(--text-ter)]',
    iconBg: 'bg-[var(--slate-dim)] border-[var(--slate)]',
    border: 'border-[var(--slate)] hover:border-[var(--slate)]',
    tag: 'Governed',
  },
  {
    title: 'Sessions',
    description: 'Understand session types, configure and run ranking sessions, and work with the immutable session results they produce.',
    href: '/prioritization/documentation/sessions',
    icon: Users,
    color: 'text-[var(--text-ter)]',
    iconBg: 'bg-[var(--slate-dim)] border-[var(--slate)]',
    border: 'border-[var(--slate)] hover:border-[var(--slate)]',
    tag: 'Work lane',
  },
  {
    title: 'Tracks',
    description: 'Learn how Tracks define comparability boundaries and how they bring items, criteria, and sessions together inside a cycle.',
    href: '/prioritization/documentation/tracks',
    icon: Workflow,
    color: 'text-[var(--text-ter)]',
    iconBg: 'bg-[var(--slate-dim)] border-[var(--slate)]',
    border: 'border-[var(--slate)] hover:border-[var(--slate)]',
    tag: 'Comparability',
  },
  {
    title: 'Catchball & Governance',
    description: 'Authority choreography: requests flow down, proposals flow up, and governance events record every transition.',
    href: '/prioritization/documentation/catchball',
    icon: ArrowRightLeft,
    color: 'text-[var(--mustard)]',
    iconBg: 'bg-[var(--mustard-dim)] border-[var(--mustard)]',
    border: 'border-[var(--mustard)] hover:border-[var(--mustard)]',
    tag: 'Ball lane',
  },
  {
    title: 'Automations',
    description: 'Rules that take the next step when something changes: notify people, assign an owner, move, tag, change state, run an agent.',
    href: '/prioritization/documentation/automations',
    icon: Zap,
    color: 'text-[var(--text-ter)]',
    iconBg: 'bg-[var(--slate-dim)] border-[var(--slate)]',
    border: 'border-[var(--slate)] hover:border-[var(--slate)]',
    tag: 'Admins',
  },
  {
    title: 'API Reference',
    description: 'Integrate with the Priorities.ai platform. Authenticate, query items, trigger sessions, and receive webhook events.',
    href: '/prioritization/documentation/api',
    icon: Code2,
    color: 'text-[var(--text-sec)]',
    iconBg: 'bg-[var(--surface)] border-[var(--rule)]',
    border: 'border-[var(--rule)] hover:border-[var(--rule)]',
    tag: 'Developer',
  },
];

const quickPaths = [
  { label: 'Submit an item', href: '/prioritization/intake/submit', external: false },
  { label: 'Run a session', href: '/prioritization/documentation/sessions', external: false },
  { label: 'Design a cycle', href: '/prioritization/documentation/cycles', external: false },
  { label: 'Understand catchball', href: '/prioritization/documentation/catchball', external: false },
  { label: 'Use the API', href: '/prioritization/documentation/api', external: false },
  { label: 'Browse the glossary', href: '/prioritization/documentation/glossary', external: false },
];

export default function DocHub() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 bg-primary-50 border border-primary-200 flex items-center justify-center">
            <BookOpen className="h-4 w-4 text-primary-600" />
          </div>
          <h1 className="text-xl font-semibold text-[var(--text-pri)] font-heading">Documentation</h1>
        </div>
        <p className="text-sm text-[var(--text-ter)] ml-11">
          Peak Priorities — visual guides, how-to references, and API documentation by functional area.
        </p>
      </div>

      {/* System Map */}
      <div>
        <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">How the system works</p>
        <SystemMapDiagram />
      </div>

      {/* Quick paths */}
      <div>
        <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-3">Quick paths</p>
        <div className="flex flex-wrap gap-2">
          {quickPaths.map((p) => (
            <Link
              key={p.href}
              to={p.href}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-primary-400 hover:text-primary-700 transition-colors"
            >
              <Search className="h-3 w-3" />
              {p.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Area cards */}
      <div>
        <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-3">Documentation by area</p>
        <div className="grid gap-4 md:grid-cols-2">
          {areas.map((area) => {
            const Icon = area.icon;
            return (
              <Link
                key={area.href}
                to={area.href}
                className={`group bg-[var(--bg-raised)]  border p-5 transition-all ${area.border}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-9 h-9  border flex items-center justify-center flex-shrink-0 transition-colors ${area.iconBg}`}>
                    <Icon className={`h-4 w-4 ${area.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-sm font-semibold text-[var(--text-pri)] group-hover:text-[var(--text-sec)] font-heading">{area.title}</h3>
                      <span className="text-xs text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-0.5 rounded-full">{area.tag}</span>
                    </div>
                    <p className="text-sm text-[var(--text-ter)] leading-relaxed">{area.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] flex-shrink-0 transition-colors mt-0.5" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Reference + Flows row */}
      <div className="grid gap-4 md:grid-cols-2">
        <Link
          to="/prioritization/documentation/flows"
          className="group bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--rule)] p-5 transition-all"
        >
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 border border-[var(--rule)] bg-[var(--surface)] flex items-center justify-center flex-shrink-0">
              <Workflow className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] font-heading mb-0.5">System Flows</h3>
              <p className="text-sm text-[var(--text-ter)]">Interactive flow diagrams — item lifecycle, cycle execution, and more.</p>
            </div>
            <ChevronRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] flex-shrink-0" />
          </div>
        </Link>

        <Link
          to="/prioritization/documentation/glossary"
          className="group bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--rule)] p-5 transition-all"
        >
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 border border-[var(--rule)] bg-[var(--surface)] flex items-center justify-center flex-shrink-0">
              <FileText className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] font-heading mb-0.5">Glossary</h3>
              <p className="text-sm text-[var(--text-ter)]">Master reference for all terms, concepts, and canonical definitions.</p>
            </div>
            <ChevronRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] flex-shrink-0" />
          </div>
        </Link>
      </div>

      {/* Coming soon: By Role */}
      <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)]/50 p-5">
        <div className="flex items-center gap-3 mb-2">
          <Users className="h-4 w-4 text-[var(--text-ter)]" />
          <h3 className="text-sm font-semibold text-[var(--text-ter)] font-heading">By Role — coming next</h3>
        </div>
        <p className="text-sm text-[var(--text-ter)] mb-3">Curated documentation paths tailored to how you use the system.</p>
        <div className="flex flex-wrap gap-2">
          {['Contributor', 'Facilitator', 'Governance Lead', 'Developer / Integrator', 'Workspace Admin'].map((role) => (
            <span key={role} className="inline-flex items-center px-3 py-1 text-xs text-[var(--text-ter)] bg-[var(--bg-raised)] border border-[var(--rule)]">
              {role}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
